import QualityComponentInterface from "@app/Interface/QualityComponentInterface";
import QualityComponentsAverage from "@app/Interface/QualityComponentsAverageInterface";
import axios from "@app/Security/AxiosAuthentication";
import ApiEndpoints from "@app/utils/ApiEndpoints";
import RollbarHelper from "@app/utils/RollbarHelper";
import { AxiosError } from "axios";

export default class QualityComponentHelper {

    /**
     * Gets quality components based on parameters
     * 
     * @param controller AbortController - handles when component unmounts
     * @param fpuId      Currently selected FPU
     * @param pageId     Page ID is used to determine permission access
     * @param reportType "", qualityComponents, e-statements, e-statementsCount, dashboardMTD, dashboardRecent
     * @param limit      Number of records to return
     * @returns 
     */
    static getQualityComponents = async (controller: AbortController, fpuId: number, pageId: number, reportType: string, limit?: number): Promise<QualityComponentInterface[]> => {

        const baseUrl: string = process.env.API_BASEURL ?? "";

        let endPoint: string = ApiEndpoints.QUALITY_COMPONENTS + `?page=1&fpu_id=${fpuId}&page_id=${pageId}`;

        if (reportType) {
            endPoint += `&report_type=${reportType}`;
        }

        const url = baseUrl + endPoint;

        return await axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                let components: QualityComponentInterface[] = response.data["hydra:member"];

                // check for record limit
                if (limit && limit > 1 && limit < components.length) {

                    components = QualityComponentHelper.getComponentsByLimit(components, limit);

                }

                return components;

            })
            .catch( (error: Error | AxiosError) => {

                RollbarHelper.logRequestError(error, "getQualityComponents()");

                // return an empty set of navItems
                const emptyItems: Array<QualityComponentInterface> = [];

                return emptyItems;

            });

    }

    /**
     * gets the first set of records based on limit parameters
     * 
     * @param components 
     * @param limit 
     * @returns 
     */
    static getComponentsByLimit = (components: QualityComponentInterface[], limit: number): QualityComponentInterface[] => {

        const limitedComponents = components.slice(0, limit);

        return limitedComponents;

    }

    /**
     * Gets Quality components month to date averages
     * for use with dashboard Month to Date component
     * 
     * @param controller AbortController - Used when component is unmounted
     * @param fpuId  Current FPU selected
     * @param pageId Page id that has permission to view daily-components
     * @returns 
     */
    static getMonthToDateAverages = async (controller: AbortController, fpuId: number, pageId: number): Promise<QualityComponentsAverage> => {
        
        const currentDate = new Date();

        const pickupStartDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

        const startDateString = pickupStartDate.toISOString();

        const baseUrl: string = process.env.API_BASEURL ?? "";

        const endPoint: string = ApiEndpoints.QUALITY_COMPONENTS + `?page=1&fpu_id=${fpuId}&page_id=${pageId}&report_type=dashboardMTD&pickup_date_start=${startDateString}`;
        
        const url = baseUrl + endPoint;

        return await axios.get(url, {
            withCredentials: true,
            signal: controller.signal
        })
            .then(response => {

                const averages: QualityComponentsAverage[] = response.data["hydra:member"];

                let average: QualityComponentsAverage;

                if (averages.length) {
                    average = averages[0];
                } else {
                    average = {
                        AvgPI: 0,
                        AvgRaw: 0,
                    };
                }

                return average;

            })
            .catch( (error: Error | AxiosError) => {

                RollbarHelper.logRequestError(error, "getMonthToDateAverages()");

                // return an empty set of navItems
                const emptyItem: QualityComponentsAverage = {
                    AvgRaw: 0,
                    AvgPI: 0
                };

                return emptyItem;

            });
    }

    static hasLpcData = (items: QualityComponentInterface[]): boolean => {

        const hasData = items.some(item => item.LPC_TEST);

        return hasData;

    }

}